import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  Library,
  Sparkles,
  GitBranch,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Package,
  AlertCircle,
  Loader2,
  X,
  Code,
  CheckCircle2,
  AlertTriangle,
  Archive,
  ExternalLink,
  Tag,
  Globe,
  Lock,
} from 'lucide-react';
import { useSkillRegistryStore, DEFAULT_ORG } from '@/stores/skillRegistry';
import type { RegisteredSkill, SkillVersionRecord } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function StatusBadge({ status }: { status: RegisteredSkill['status'] }) {
  const cfg = {
    published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    draft: 'bg-amber-100 text-amber-700 border-amber-200',
    deprecated: 'bg-slate-100 text-slate-500 border-slate-200',
    archived: 'bg-red-50 text-red-400 border-red-100',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border uppercase tracking-wide',
        cfg[status],
      )}
    >
      {status}
    </span>
  );
}

const TOOL_LABELS: Record<string, string> = {
  get_creative_brief: 'Brief',
  generate_creative_angles: 'Angles',
  generate_ugc_script: 'Script',
  generate_video_prompts: 'Video',
  get_ad_pack: 'Ad Pack',
};

// ---------------------------------------------------------------------------
// Binding Inspector
// ---------------------------------------------------------------------------

function BindingInspector({ version }: { version: SkillVersionRecord }) {
  const b = version.bindings;
  const defaultPayload = {
    persona_id: 'persona_lunchbox_strategist',
    client_uid: 'skoutorganic',
  };
  const mergedPayload = {
    ...defaultPayload,
    ...(b.brief_config ? { template_config: b.brief_config } : {}),
    ...(b.angle_config ? { angle_config: b.angle_config } : {}),
    ...(b.script_config ? { script_config: b.script_config } : {}),
    ...(Object.keys(b.prompt_overrides).length ? { prompt_overrides: b.prompt_overrides } : {}),
  };

  const hasBinding =
    b.brief_config || b.angle_config || b.script_config || Object.keys(b.prompt_overrides).length > 0;

  return (
    <div className="space-y-4">
      {/* Binding summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
          <p className="text-[10px] font-mono text-slate-400 mb-1 uppercase tracking-wide">Produces</p>
          <div className="flex flex-wrap gap-1">
            {b.produces.length ? (
              b.produces.map((p) => (
                <span key={p} className="text-[10px] bg-brand-teal/10 text-brand-teal px-1.5 py-0.5 rounded font-mono">
                  {p}
                </span>
              ))
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
          <p className="text-[10px] font-mono text-slate-400 mb-1 uppercase tracking-wide">Consumes</p>
          <div className="flex flex-wrap gap-1">
            {b.consumes.length ? (
              b.consumes.map((c) => (
                <span key={c} className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-mono">
                  {c}
                </span>
              ))
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
          </div>
        </div>
      </div>

      {/* Prompt overrides */}
      {Object.keys(b.prompt_overrides).length > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wide text-amber-600 font-semibold">
            Prompt Overrides
          </p>
          {Object.entries(b.prompt_overrides).map(([k, v]) => (
            <div key={k} className="space-y-0.5">
              <p className="text-[10px] font-mono text-amber-500">{k}</p>
              <p className="text-xs text-slate-700 bg-white rounded border border-amber-100 px-2 py-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Payload diff */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-1.5">
            Default payload
          </p>
          <pre className="text-[11px] font-mono bg-slate-50 border border-slate-100 rounded-lg p-3 overflow-auto max-h-52 text-slate-600">
            {JSON.stringify(defaultPayload, null, 2)}
          </pre>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wide text-brand-teal mb-1.5">
            Merged payload (with binding)
          </p>
          <pre className="text-[11px] font-mono bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-3 overflow-auto max-h-52 text-slate-700">
            {JSON.stringify(mergedPayload, null, 2)}
          </pre>
        </div>
      </div>

      {!hasBinding && (
        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          <AlertTriangle size={14} />
          No binding configured yet — this skill will use platform defaults for all tool calls.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version Timeline
// ---------------------------------------------------------------------------

function VersionTimeline({
  versions,
  loading,
  onSelect,
  selected,
}: {
  versions: SkillVersionRecord[];
  loading: boolean;
  onSelect: (v: SkillVersionRecord) => void;
  selected: SkillVersionRecord | null;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
        <Loader2 size={14} className="animate-spin" />
        Loading versions…
      </div>
    );
  }
  if (!versions.length) {
    return <p className="text-xs text-slate-400 py-4">No published versions yet.</p>;
  }
  return (
    <div className="space-y-1.5">
      {versions.map((v, i) => (
        <button
          key={v.version}
          onClick={() => onSelect(v)}
          className={clsx(
            'w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border text-left transition-all',
            selected?.version === v.version
              ? 'bg-brand-teal/5 border-brand-teal/30 text-navy-900'
              : 'bg-white border-slate-100 hover:border-slate-200 text-slate-700',
          )}
        >
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0 mt-0.5">
            <div
              className={clsx(
                'w-2 h-2 rounded-full flex-shrink-0',
                i === 0 ? 'bg-emerald-500' : 'bg-slate-300',
              )}
            />
            {i < versions.length - 1 && <div className="w-px h-5 bg-slate-200" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold">{v.version}</span>
              {i === 0 && (
                <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold uppercase">
                  Latest
                </span>
              )}
            </div>
            {v.changelog && (
              <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{v.changelog}</p>
            )}
            <p className="text-[10px] text-slate-400 mt-0.5">
              {v.published_by && <span>{v.published_by} · </span>}
              {timeAgo(v.published_at)}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill Detail Drawer
// ---------------------------------------------------------------------------

function SkillDrawer({
  skill,
  onClose,
}: {
  skill: RegisteredSkill;
  onClose: () => void;
}) {
  const { versions, selectedVersion, versionsLoading, fetchVersions, selectVersion } =
    useSkillRegistryStore();
  const [tab, setTab] = useState<'overview' | 'versions' | 'binding'>('overview');

  useEffect(() => {
    fetchVersions(skill.skill_id);
  }, [skill.skill_id, fetchVersions]);

  useEffect(() => {
    if (versions.length > 0 && !selectedVersion) {
      selectVersion(versions[0]);
    }
  }, [versions, selectedVersion, selectVersion]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 bg-slate-50">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{skill.icon ?? '⚙️'}</span>
              <h2 className="text-lg font-bold text-navy-900">{skill.name}</h2>
              <StatusBadge status={skill.status} />
            </div>
            <p className="text-xs text-slate-500">{skill.skill_id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6">
          {(['overview', 'versions', 'binding'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={clsx(
                'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors capitalize',
                tab === t
                  ? 'border-brand-teal text-brand-teal'
                  : 'border-transparent text-slate-500 hover:text-navy-900',
              )}
            >
              {t === 'binding' ? 'Binding Inspector' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {tab === 'overview' && (
            <>
              {skill.description && <p className="text-sm text-slate-600">{skill.description}</p>}

              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Version" value={skill.latest_version} mono />
                <InfoRow label="Visibility" value={skill.visibility} icon={skill.visibility === 'org' ? Globe : Lock} />
                <InfoRow label="Created by" value={skill.created_by || '—'} />
                <InfoRow label="Last updated" value={timeAgo(skill.updated_at)} />
                <InfoRow label="Installs" value={String(skill.install_count)} />
                <InfoRow label="Last used" value={timeAgo(skill.last_used_at)} />
              </div>

              {/* Required tools */}
              {skill.required_tools.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-2">
                    Required MCP Tools
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {skill.required_tools.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1 text-[10px] font-mono bg-navy-950/5 text-navy-700 border border-navy-200/30 px-2 py-1 rounded"
                      >
                        <Code size={9} />
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {skill.tags.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {skill.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                      >
                        <Tag size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Client scopes */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-2">Client Scopes</p>
                {skill.client_scopes.length === 0 ? (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <Globe size={12} />
                    All org clients (no restriction)
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {skill.client_scopes.map((c) => (
                      <span key={c} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'versions' && (
            <VersionTimeline
              versions={versions}
              loading={versionsLoading}
              onSelect={selectVersion}
              selected={selectedVersion}
            />
          )}

          {tab === 'binding' && (
            <>
              {versionsLoading ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                  <Loader2 size={14} className="animate-spin" />
                  Loading binding…
                </div>
              ) : selectedVersion ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-slate-600">
                      Binding for{' '}
                      <span className="font-mono font-semibold">{selectedVersion.version}</span>
                    </p>
                    {versions.length > 1 && (
                      <select
                        className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                        value={selectedVersion.version}
                        onChange={(e) => {
                          const v = versions.find((vv) => vv.version === e.target.value);
                          if (v) selectVersion(v);
                        }}
                      >
                        {versions.map((v) => (
                          <option key={v.version} value={v.version}>
                            {v.version}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <BindingInspector version={selectedVersion} />
                </>
              ) : (
                <p className="text-xs text-slate-400 py-4">No version selected.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
  icon: Icon,
}: {
  label: string;
  value: string;
  mono?: boolean;
  icon?: React.ElementType;
}) {
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-100 px-3 py-2.5">
      <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
      <p className={clsx('text-xs text-navy-800 font-medium flex items-center gap-1', mono && 'font-mono')}>
        {Icon && <Icon size={11} className="text-slate-400" />}
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const STATUS_FILTERS = ['all', 'published', 'draft', 'deprecated', 'archived'] as const;

export function SkillLibraryPage() {
  const { skills, loading, error, fetchSkills, selectSkill, selectedSkill } = useSkillRegistryStore();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSkills(statusFilter === 'all' ? undefined : statusFilter);
  }, [fetchSkills, statusFilter]);

  const filtered = skills.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.skill_id.toLowerCase().includes(q) ||
      s.name.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  const published = skills.filter((s) => s.status === 'published').length;
  const draft = skills.filter((s) => s.status === 'draft').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
          <Library size={22} className="text-brand-teal" />
          Skill Library
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Versioned creative skills for <span className="font-mono text-xs">{DEFAULT_ORG}</span>.
          Each skill defines the server-side binding applied to every MCP tool call.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <StatChip label="Total skills" value={skills.length} color="text-navy-900" />
        <StatChip label="Published" value={published} color="text-emerald-600" />
        <StatChip label="Draft" value={draft} color="text-amber-600" />
        <StatChip label="Org" value={DEFAULT_ORG} color="text-brand-teal" mono />
      </div>

      {/* Marketplace strip */}
      <div className="flex items-center gap-3 bg-navy-950 rounded-xl px-4 py-3">
        <Package size={16} className="text-brand-teal flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white">Marketplace endpoint</p>
          <p className="text-[11px] font-mono text-slate-400 truncate">
            {window.location.origin}/api/v1/organizations/{DEFAULT_ORG}/skills/marketplace.json
          </p>
        </div>
        <a
          href={`/api/v1/organizations/${DEFAULT_ORG}/skills/marketplace.json`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-[10px] text-brand-teal hover:text-white transition-colors flex-shrink-0"
        >
          <ExternalLink size={12} />
          Open
        </a>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={clsx(
                'px-3 py-1 text-xs font-medium rounded-md transition-colors capitalize',
                statusFilter === f ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500 hover:text-navy-900',
              )}
            >
              {f}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search skills…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-48 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading skills…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState search={search} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((skill) => (
            <SkillCard key={skill.skill_id} skill={skill} onClick={() => selectSkill(skill)} />
          ))}
        </div>
      )}

      {selectedSkill && (
        <SkillDrawer skill={selectedSkill} onClose={() => selectSkill(null)} />
      )}
    </div>
  );
}

function StatChip({
  label,
  value,
  color,
  mono,
}: {
  label: string;
  value: string | number;
  color: string;
  mono?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-4 py-3">
      <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{label}</p>
      <p className={clsx('text-lg font-bold mt-0.5', color, mono && 'text-xs font-mono mt-1 leading-none')}>
        {value}
      </p>
    </div>
  );
}

function SkillCard({ skill, onClick }: { skill: RegisteredSkill; onClick: () => void }) {
  const toolPills = skill.required_tools.slice(0, 3);
  const moreTools = skill.required_tools.length - 3;

  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-100 rounded-xl p-4 text-left hover:border-brand-teal/40 hover:shadow-soft transition-all group flex flex-col gap-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{skill.icon ?? '⚙️'}</span>
          <div>
            <h3 className="text-sm font-semibold text-navy-900 leading-tight">{skill.name}</h3>
            <p className="text-[10px] font-mono text-slate-400">{skill.skill_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusBadge status={skill.status} />
          <ChevronRight size={14} className="text-slate-300 group-hover:text-brand-teal transition-colors" />
        </div>
      </div>

      {skill.description && (
        <p className="text-xs text-slate-500 line-clamp-2">{skill.description}</p>
      )}

      <div className="flex items-center justify-between mt-auto pt-1 border-t border-slate-50">
        <div className="flex items-center gap-1 flex-wrap">
          {toolPills.map((t) => (
            <span key={t} className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              {TOOL_LABELS[t] ?? t}
            </span>
          ))}
          {moreTools > 0 && (
            <span className="text-[9px] text-slate-400">+{moreTools}</span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-400">
          <GitBranch size={10} />
          {skill.latest_version}
          <Clock size={10} className="ml-1" />
          {timeAgo(skill.updated_at)}
        </div>
      </div>
    </button>
  );
}

function EmptyState({ search }: { search: string }) {
  return (
    <div className="text-center py-16 space-y-3">
      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
        <Sparkles size={20} className="text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-600">
        {search ? `No skills match "${search}"` : 'No skills in this registry yet'}
      </p>
      <p className="text-xs text-slate-400 max-w-xs mx-auto">
        {search
          ? 'Try a different search term or status filter.'
          : 'Publish your first skill with `agent.sh skills publish` to get started.'}
      </p>
    </div>
  );
}
